import React, { useState } from 'react';

const EmployeeFeedback = ({ employees, feedbacks, userRole, onAddFeedback }) => {
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [rating, setRating] = useState(5);
  const [showForm, setShowForm] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  const canGiveFeedback = ['hr', 'admin', 'superadmin', 'manager'].includes(userRole);
  
  // Get user's own feedbacks (for employees)
  const myFeedbacks = canGiveFeedback ? [] : feedbacks.filter(f => f.employeeId === JSON.parse(localStorage.getItem('user') || '{}').id);

  const handleSubmitFeedback = (e) => {
    e.preventDefault();
    if (!selectedEmployee || !feedbackText) {
      alert('Please select employee and enter feedback');
      return;
    }
    
    onAddFeedback({
      employeeId: parseInt(selectedEmployee),
      employeeName: employees.find(e => e.id === parseInt(selectedEmployee))?.name,
      feedback: feedbackText,
      rating: rating,
      reviewer: JSON.parse(localStorage.getItem('user') || '{}').name || 'Admin'
    });
    
    setSelectedEmployee('');
    setFeedbackText('');
    setRating(5);
    setShowForm(false);
    alert('Feedback submitted successfully!');
  };

  const getRatingStars = (ratingValue) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map(star => (
          <span key={star} className={star <= ratingValue ? 'text-yellow-400' : 'text-gray-300'}>★</span>
        ))}
      </div>
    );
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 3.5) return 'text-blue-600';
    if (rating >= 2.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Feedback Form for HR/Admin/Manager */}
      {canGiveFeedback && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50 flex justify-between items-center">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Give Employee Feedback</h2>
              <p className="text-sm text-gray-600">Provide constructive feedback to help employees grow</p>
            </div>
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              {showForm ? 'Cancel' : '+ Give Feedback'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleSubmitFeedback} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Employee *</label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required
                  >
                    <option value="">Select Employee</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name} ({emp.designation})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Rating *</label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        onClick={() => setRating(star)}
                        className="text-2xl focus:outline-none"
                      >
                        <span className={(hoveredRating || rating) >= star ? 'text-yellow-400' : 'text-gray-300'}>★</span>
                      </button>
                    ))}
                    <span className="ml-2 text-sm text-gray-600">{rating}/5</span>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Feedback *</label>
                  <textarea
                    rows="4"
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Write your feedback here... (What went well? Areas for improvement?)"
                    required
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-6 mt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                >
                  Submit Feedback
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Recent Feedbacks List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {canGiveFeedback ? 'Recent Employee Feedbacks' : 'My Feedbacks'}
          </h2>
          <p className="text-sm text-gray-600">
            {canGiveFeedback ? 'Latest feedback from team members' : 'Feedback received from managers and peers'}
          </p>
        </div>

        <div className="divide-y divide-gray-100">
          {(canGiveFeedback ? feedbacks : myFeedbacks).length > 0 ? (
            (canGiveFeedback ? feedbacks : myFeedbacks).map(feedback => {
              const employee = employees.find(e => e.id === feedback.employeeId);
              return (
                <div key={feedback.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 font-bold">
                          {feedback.employeeName?.charAt(0) || 'E'}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-800">{feedback.employeeName}</h3>
                          <p className="text-xs text-gray-500">{employee?.designation} • {employee?.department}</p>
                        </div>
                      </div>
                      <div className="ml-12">
                        <div className="flex items-center gap-2 mb-2">
                          {getRatingStars(feedback.rating)}
                          <span className={`text-sm font-medium ${getRatingColor(feedback.rating)}`}>
                            {feedback.rating}/5
                          </span>
                        </div>
                        <p className="text-gray-700 text-sm leading-relaxed">{feedback.feedback}</p>
                        <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                          <span>Reviewed by: {feedback.reviewer}</span>
                          <span>•</span>
                          <span>{new Date(feedback.date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <p className="text-gray-500">No feedback available yet</p>
              {canGiveFeedback && (
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-4 text-indigo-600 hover:text-indigo-700"
                >
                  Give your first feedback →
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Feedback Statistics */}
      {canGiveFeedback && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h3 className="text-md font-semibold text-gray-800 mb-4">Feedback Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600">{feedbacks.length}</p>
              <p className="text-xs text-gray-500">Total Feedbacks</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {(feedbacks.reduce((sum, f) => sum + f.rating, 0) / (feedbacks.length || 1)).toFixed(1)}
              </p>
              <p className="text-xs text-gray-500">Average Rating</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">
                {feedbacks.filter(f => f.rating >= 4).length}
              </p>
              <p className="text-xs text-gray-500">Excellent (4-5★)</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">
                {new Set(feedbacks.map(f => f.employeeId)).size}
              </p>
              <p className="text-xs text-gray-500">Employees Reviewed</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeFeedback;